import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HiChevronLeft as ArrowLeft } from 'react-icons/hi2';
import { Button } from '@knack/asterisk-react';

import { type SampleApp } from '@/types/apps';
import { useSetupWizardLogic } from '@/hooks/useSetupWizardLogic';
import { FullPageSpinner } from '@/components/ui/FullPageSpinner';
import { AiModal } from '@/pages/apps/create-app/ai-form/AiModal';
import CreateAppFromSampleModal from '@/pages/apps/create-app/CreateAppFromSampleModal';
import { AppCreationErrorBanner } from '@/pages/apps/create-app/setup-wizard-variations/AppCreationErrorBanner';
import { TemplateCardHorizontalUI } from './TemplateCardHorizontalUI';

interface StepSelectCardProps {
  selectedIndustry: string;
  sampleApps: SampleApp[];
  onGoBack: () => void;
}

export function StepSelectCardHorizontalUI({
  selectedIndustry,
  sampleApps,
  onGoBack
}: StepSelectCardProps) {
  const [t] = useTranslation();
  const {
    handleAppCreation,
    appCreationError,
    shouldShowAiModal,
    setShouldShowAiModal,
    isPending
  } = useSetupWizardLogic(selectedIndustry, '', sampleApps);

  const [shouldShowAppSampleModal, setShouldShowAppSampleModal] = useState(false);

  // Creates a shuffled list of app creation options (import, AI, template).
  const shuffledCards = useMemo(() => {
    const cards = [
      {
        type: 'import' as const,
        onClick: () => handleAppCreation('import')
      },
      {
        type: 'ai' as const,
        onClick: () => handleAppCreation('ai')
      },
      {
        type: 'template' as const,
        onClick: () => setShouldShowAppSampleModal(true)
      }
    ];
    // Randomize the order of cards (0.5 shifts the result to allow random sorting).
    return cards.sort(() => Math.random() - 0.5);
  }, []);

  if (isPending) {
    return <FullPageSpinner />;
  }

  return (
    <div className="mt-10 flex flex-col items-center gap-6" data-testid="step-select-card">
      {appCreationError && (
        <AppCreationErrorBanner
          appCreationError={appCreationError}
          data-testid="app-creation-error-banner"
        />
      )}

      {shouldShowAiModal && (
        <AiModal open onClose={() => setShouldShowAiModal(false)} data-testid="ai-modal" />
      )}

      <CreateAppFromSampleModal
        open={shouldShowAppSampleModal}
        setIsOpen={setShouldShowAppSampleModal}
        variationId="horizontal_stack_ui"
        defaultTab={selectedIndustry}
      />

      <div className="grid w-full gap-4 sm:grid-cols-[repeat(auto-fit,_minmax(400px,_1fr))]">
        {shuffledCards.map((card) => (
          <TemplateCardHorizontalUI key={card.type} type={card.type} onClick={card.onClick} />
        ))}
      </div>

      <div className="mb-4 flex w-full">
        <Button
          intent="minimal"
          className="justify-start"
          onClick={onGoBack}
          data-testid="back-button"
        >
          <ArrowLeft />
          {t('actions.back')}
        </Button>
      </div>
    </div>
  );
}
